import { getConfigOrThrow } from 'gossamer-front-end';
import { UserRegisterRequest, UserUpgradeFromFreeRequest } from 'cb-model';

export type PlanOptionId = 'personal' | 'pro' | 'premium' | 'enterprise';
export type BillingInterval = 'monthly' | 'yearly';

export interface RegistrationDraft {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  openInviteInstitutionId?: number;
  planOptionId?: PlanOptionId;
  billingInterval: BillingInterval;
  institutionName: string;
  subscriptionStatus: 'not_started' | 'pending' | 'active';
  subscriptionCheckoutSessionId?: string;
  redirectToPath?: string;
}

export interface EmailRegistrationStatus {
  emailAddressAlreadyExists: boolean;
  openInviteInstitutionId?: number | null;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
}

export const REGISTRATION_DRAFT_STORAGE_KEY = 'cb:userRegistrationDraft';

// Survives client-side steps only; a checkout return or reload requires password re-entry.
let registrationPassword = '';

const persistedRegistrationDraft = (
  draft: Partial<RegistrationDraft>,
): Omit<RegistrationDraft, 'password'> => ({
  firstName: draft.firstName ?? '',
  lastName: draft.lastName ?? '',
  email: draft.email ?? '',
  openInviteInstitutionId: draft.openInviteInstitutionId,
  planOptionId: draft.planOptionId,
  billingInterval: draft.billingInterval === 'yearly' ? 'yearly' : 'monthly',
  institutionName: draft.institutionName ?? '',
  subscriptionStatus: draft.subscriptionStatus ?? 'not_started',
  subscriptionCheckoutSessionId: draft.subscriptionCheckoutSessionId,
  redirectToPath: draft.redirectToPath,
});

export const makeEmptyRegistrationDraft = (): RegistrationDraft => ({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  billingInterval: 'monthly',
  institutionName: '',
  subscriptionStatus: 'not_started',
});

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const backendOrigin = getConfigOrThrow('gossamer-be-origin').replace(/\/$/, '');
  const response = await fetch(`${backendOrigin}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorBody;
  if (!response.ok) {
    throw new Error(body.error || body.message || `Request failed with status ${response.status}`);
  }
  return body;
};

export const getEmailRegistrationStatus = async (email: string): Promise<EmailRegistrationStatus> =>
  request(`/user/email-address-registration-status?emailAddress=${encodeURIComponent(email)}`);

export const createSubscriptionCheckoutSession = async (
  draft: RegistrationDraft,
): Promise<{ url?: string; sessionId?: string }> =>
  request('/gossamer/main/subscription/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({
      planType: draft.planOptionId,
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      institutionName: draft.institutionName,
      billingInterval: draft.billingInterval,
      purpose: 'registration',
      returnPath: '/auth/user-registration/institution-name',
    }),
  });

export const getSubscriptionCheckoutSessionStatus = async (
  sessionId: string,
): Promise<{ checkoutStatus?: string; paymentStatus?: string; error?: string }> =>
  request(
    `/gossamer/main/subscription/checkout-session-status?sessionId=${encodeURIComponent(sessionId)}`,
  );

export const registerUser = async (body: UserRegisterRequest): Promise<void> => {
  await request('/gossamer/main/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const sendContactMessage = async (body: {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}): Promise<void> => {
  await request('/email/send-email', {
    method: 'POST',
    body: JSON.stringify({ ...body, phoneNumber: '' }),
  });
};

export const sendEnterpriseEnquiry = async (draft: RegistrationDraft, enquiry: string): Promise<void> =>
  sendContactMessage({
    firstName: draft.firstName,
    lastName: draft.lastName,
    email: draft.email,
    message: [
      'Enterprise plan enquiry',
      '',
      `Name: ${draft.firstName} ${draft.lastName}`,
      `Email: ${draft.email}`,
      '',
      'Enquiry:',
      enquiry,
    ].join('\n'),
  });

export const createUpgradeCheckoutSession = async (body: {
  planType: 'pro' | 'premium';
  firstName: string;
  lastName: string;
  email: string;
  institutionName: string;
  billingInterval: BillingInterval;
  returnPath: string;
}): Promise<{ url?: string; sessionId?: string }> =>
  request('/gossamer/main/subscription/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ ...body, purpose: 'upgrade' }),
  });

export const upgradeFromFree = async (body: UserUpgradeFromFreeRequest): Promise<void> => {
  await request('/gossamer/main/upgrade-from-free', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const readRegistrationDraft = (): RegistrationDraft => {
  const value = sessionStorage.getItem(REGISTRATION_DRAFT_STORAGE_KEY);
  if (!value) return makeEmptyRegistrationDraft();

  const parsed = JSON.parse(value) as Partial<RegistrationDraft>;
  const persisted = persistedRegistrationDraft(parsed);
  if ('password' in parsed) {
    sessionStorage.setItem(REGISTRATION_DRAFT_STORAGE_KEY, JSON.stringify(persisted));
  }
  return {
    ...persisted,
    password: registrationPassword,
  };
};

export const writeRegistrationDraft = (draft: RegistrationDraft): void => {
  sessionStorage.setItem(
    REGISTRATION_DRAFT_STORAGE_KEY,
    JSON.stringify(persistedRegistrationDraft(draft)),
  );
  registrationPassword = draft.password;
};

export const clearRegistrationDraft = (): void => {
  registrationPassword = '';
  sessionStorage.removeItem(REGISTRATION_DRAFT_STORAGE_KEY);
};
